import { ColorKeys } from '@/styles/colors';
import { ICarouselInfo } from '@/components/Carousel/types';
import {
  createCategoryCarouselImage,
  createContent1ResponsiveImage,
  createContent2ResponsiveImage,
  createStatResponsiveImage,
} from '@/utils/responsiveImageHelpers';

type HeroTextColor = 'white' | 'black';

const stat1ResponsiveImage = createStatResponsiveImage;
const categoryCarouselImage = createCategoryCarouselImage;
const content1ResponsiveImage = createContent1ResponsiveImage;
const content2ResponsiveImage = createContent2ResponsiveImage;

const data = {
  layout: {
    data: {
      title: 'About Brilliant Earth | Jewelry Redefined',
      description: 'This is jewelry redefined. Join us in transforming the jewelry industry for good.',
      keywords: 'canadian diamonds, conflict-free diamonds, conflict free diamonds, canada diamonds',
      canonical: 'https://sandbox3.brilliantearth.com/about/',
      pageGroup: 'Mission',
      breadcrumbSchema: {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://sandbox3.brilliantearth.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'About',
            item: 'https://sandbox3.brilliantearth.com/about/',
          },
        ],
      },
    },
  },

  breadcrumbs: {
    pageName: 'About',
    crumbs: [],
  },

  hero: {
    isHeader: true,
    description:
      'We believe that the jewelry you wear is an expression of who you are. So we approach each design as a unique work of art, carefully considering every detail.',
    textColor: 'black' as HeroTextColor,
    imgAlt: 'About Us Hero Banner Image',
    hasIcon: true,
    iconFill: '#2c2c2c',
    background: [
      {
        src: '/bee2-ui/img/mission/about/Mission_Page_About_Hero_DT.jpg',
        size: {
          width: 1440,
          height: 400,
          aspectRatio: 18 / 5,
        },
        breakpoint: 1024,
      },
    ],
    backgroundMobile: [
      {
        src: '/bee2-ui/img/mission/about/Mission_Page_About_Hero_MB.jpg?v1',
        size: {
          width: 390,
          height: 400,
          aspectRatio: 39 / 40,
        },
        breakpoint: 320,
      },
    ],
  },
  missionNavLinks: [
    {
      label: 'About',
      href: '/about/',
      isActive: true,
    },
    {
      label: 'Mission',
      href: '/about/mission/',
    },
    {
      label: 'Transparency',
      href: '/about/mission/transparency/',
    },
    {
      label: 'Sustainability',
      href: '/about/mission/sustainability/',
    },
    {
      label: 'Compassion',
      href: '/about/mission/compassion/',
    },
    {
      label: 'Inclusion',
      href: '/about/mission/inclusion/',
    },
  ],
  contentBlock1: {
    title: 'Our Beginning',
    description: `
      <p>
        When CEO Beth Gerstein began engagement ring shopping, nothing felt right. She wanted something beautiful,
        yes, but also something that aligned with her values. A ring that celebrated love and respected the planet.
        Around the same time, Eric Grossberg was following his own passion. Childhood trips to the American Museum of
        Natural History had left him fascinated by gems and the stories they carry.
      </p>
      <p>
        When these two met at business school, they realized they were asking the same question: How can we make jewelry more meaningful?
      </p>
      <p>
        In 2005, they founded Brilliant Earth with a mission to make the jewelry industry more transparent, inclusive,
        and impactful. Their vision? A dedication to detail that inspires confidence, with intention behind every choice.
      </p>
      <p>
        20 years later, that belief still guides everything we do. From our ethical sourcing practices to our award-winning
        designs and exceptional showroom experiences, we're committed to one idea: delight in the details.
      </p>
    `,
    isWistia: true,
    videoSrc: 'https://fast.wistia.net/embed/iframe/hotzxkhbpe?videoFoam=true',
    videoTitle: 'About Brilliant Earth',
    hasMediaDescription: true,
    mediaDescription: `
      <p class="!tw-mb-2">
        “To me, it has always been about doing business differently, and in doing so, inspiring others to do the same.
        I invite you to join us in this higher purpose: to do business differently, challenge the status quo, be deliberate
        about how we give back, and inspire positive impact across our entire industry.”
      </p>
      <p class="tw-text-center md:tw-text-left">— Beth Gerstein, Co-Founder & CEO</p>
    `,
    classNames: '',
    videoClassname:
      'tw-object-cover tw-w-full tw-relative [&_img]:tw-max-w-full [&_img]:tw-max-h-full tw-overflow-hidden',
    color: 'white' as ColorKeys,
    desktopMediaPosition: 'right' as any,
    mobileMediaPosition: 'bottom' as any,
    actions: null,
    autoPlay: true,
    imageSources: content1ResponsiveImage('/bee2-ui/img/mission/about/Mission_Statement_Brilliant_Earth.png'),
  },
  largeCopySection1: {
    description: `
      <p>
        We take a purposeful approach to how jewelry is sourced and crafted because we believe artistry should respect
        the people and places it comes from. For us, carefully considering every detail includes striving to leave a positive
        impact on gemstone and gold mining communities worldwide and the jewelry industry at large.
      </p>
    `,
    color: 'dark-green' as ColorKeys,
    additionalClasses: 'tw-mb-0 md:tw-mb-0 lg:tw-mb-0',
  },
  statSection1: {
    header: 'Our Mission Pillars',
    classNames: 'tw-pt-0 lg:tw-pt-0',
    tileSeparatorColor: 'light-green-light' as ColorKeys,
    tiles: [
      {
        imgSrcs: stat1ResponsiveImage(
          '/bee2-ui/img/mission/about/Mission_Page_About_Hero_Mission_Pillars1_DT.jpg',
          '/bee2-ui/img/mission/about/Mission_Page_About_Hero_Mission_Pillars1_MB.jpg'
        ),
        imgAlt: '',
        optionalHeader: 'Transparency',
        optionalBody:
          'We know where our precious metals and gemstones come from and how our ethical jewelry is made, and we share that information with you.',
        optionalLinkUrl: '/about/mission/transparency/',
        optionalLinkLabel: 'Learn More',
      },
      {
        imgSrcs: stat1ResponsiveImage(
          '/bee2-ui/img/mission/about/Mission_Page_About_Hero_Mission_Pillars2_DT.jpg',
          '/bee2-ui/img/mission/about/Mission_Page_About_Hero_Mission_Pillars2_MB.jpg'
        ),
        imgAlt: '',
        optionalHeader: 'Sustainability',
        optionalBody:
          'From our use of recycled and sustainable materials to our carbon reduction targets, we set ambitious goals and use industry-leading practices to protect the planet.',
        optionalLinkUrl: '/about/mission/sustainability/',
        optionalLinkLabel: 'Learn More',
      },
      {
        imgSrcs: stat1ResponsiveImage(
          '/bee2-ui/img/mission/about/Mission_Page_About_Hero_Mission_Pillars3_DT.jpg',
          '/bee2-ui/img/mission/about/Mission_Page_About_Hero_Mission_Pillars3_MB.jpg'
        ),
        imgAlt: '',
        optionalHeader: 'Compassion',
        optionalBody:
          'We care about global and local communities, including our employees and the people who help bring our jewelry to life, and focus on creating a positive impact through our Brilliant Earth Foundation initiatives and grants.',
        optionalLinkUrl: '/about/mission/compassion/',
        optionalLinkLabel: 'Learn More',
      },
      {
        imgSrcs: stat1ResponsiveImage(
          '/bee2-ui/img/mission/about/Mission_Page_About_Hero_Mission_Pillars4_DT.jpg',
          '/bee2-ui/img/mission/about/Mission_Page_About_Hero_Mission_Pillars4_MB.jpg'
        ),
        imgAlt: '',
        optionalHeader: 'Inclusion',
        optionalBody:
          'We support and invest in our diverse teams, fostering an environment where our employees feel they belong, grow in their careers, and take genuine pride in their work.',
        optionalLinkUrl: '/about/mission/inclusion/',
        optionalLinkLabel: 'Learn More',
      },
    ],
  },
  contentBlockSlide1: {
    title: 'Award-Winning Artistry',
    description: `
      <p>
        At Brilliant Earth, we celebrate jewelry as an art form. Our team designs each of our collections with precision,
        purpose, and a hint of the extraordinary at our San Francisco studio.
      </p>
    `,
    desktopMediaPosition: 'right' as any,
    actions: [],
    imageAlt: 'Award-Winning Artistry',
    imageSources: content1ResponsiveImage(
      '/bee2-ui/img/mission/about/Mission_Page_About_Hero_BE_Difference_1_DT.jpg',
      '/bee2-ui/img/mission/about/Mission_Page_About_Hero_BE_Difference_1_MB.jpg'
    ),
    color: 'white-grey-20' as ColorKeys,
  },
  contentBlockSlide2: {
    title: 'Uniquely Yours',
    description: `
      <p>
        Whether it’s a made-to-order, one-of-a-kind engagement ring or a personalized piece of fine jewelry, we believe jewelry
        should reflect who you are, so we create pieces that <i>celebrate your personal story</i>.
      </p>
    `,
    desktopMediaPosition: 'left' as any,
    actions: [
      {
        label: 'Learn More',
        type: 'secondary' as 'primary' | 'secondary',
        href: '/engagement-rings/shop-all/',
      },
    ],
    imageAlt: 'Uniquely Yours',
    imageSources: content1ResponsiveImage(
      '/bee2-ui/img/mission/about/Mission_Page_About_Hero_BE_Difference_2_DT.jpg',
      '/bee2-ui/img/mission/about/Mission_Page_About_Hero_BE_Difference_2_MB.jpg'
    ),
    color: 'white-grey-20' as ColorKeys,
  },
  contentBlockSlide3: {
    title: 'Unmatched Materials & Craftsmanship',
    description: `<p>Every curve, cut, and setting is <i>carefully considered</i> down to the smallest detail, beginning with our responsibly and transparently sourced precious metals and gemstones.</p>`,
    desktopMediaPosition: 'right' as any,
    actions: [],
    imageAlt: 'Unmatched Materials & Craftsmanship',
    imageSources: content1ResponsiveImage(
      '/bee2-ui/img/mission/about/Mission_Page_About_Hero_BE_Difference_3_DT.jpg',
      '/bee2-ui/img/mission/about/Mission_Page_About_Hero_BE_Difference_3_MB.jpg'
    ),
    color: 'white-grey-20' as ColorKeys,
  },
  contentBlock2: {
    title: 'We’re Here For You',
    description: `
      <p>
        Our jewelry specialists and gemologists bring decades of expertise to every interaction, creating exceptional experiences
        that extend far beyond your purchase. Each appointment is tailored to you, whether you're exploring our award-winning
        collections, exploring design possibilities to bring your vision to life, or learning about gemstone quality and traceability.
        And with lifetime services and ongoing support, we are your trusted partner for every milestone and moment.
      </p>
    `,
    desktopMediaPosition: 'right' as any,
    color: 'white' as ColorKeys,
    imageAlt: 'BE Showroom',
    imageSources: content2ResponsiveImage(
      '/bee2-ui/img/mission/about/Mission_Page_About_Hero_BE_Experience_DT.jpg',
      '/bee2-ui/img/mission/about/Mission_Page_About_Hero_BE_Experience_MB.jpg'
    ),
    actions: [
      {
        label: 'Visit a Showroom',
        color: 'dark-green' as ColorKeys,
        type: 'secondary' as 'primary' | 'secondary',
        href: '/stores/',
      },
      {
        label: 'Book a Virtual Appointment',
        color: 'dark-green' as ColorKeys,
        type: 'secondary' as 'primary' | 'secondary',
        href: '/stores/virtual-appointment/',
      },
    ],
    classNames: '[&:last-child]:tw-my-14 lg:[&:last-child]:tw-my-20',
  },
  missionGridSection: [
    {
      title: 'Free Shipping Both Ways',
      description: 'Whether your purchase is headed to you or back to us, we cover the cost of shipping every time.',
      link: '/shipping/',
      ariaLabel: 'Learn more on free shipping both ways',
      label: 'Learn More',
    },
    {
      title: 'Free 30-Day Returns & Exchanges',
      description: 'We offer free 30-day returns and exchanges to make sure your purchase is just right.',
      link: '/30-Day-Returns/',
      ariaLabel: 'Learn more on free 30-day returns and exchanges',
      label: 'Learn More',
    },
    {
      title: '24/7 Customer Support',
      description:
        'Our experts are available via chat, phone, text, and email to answer all your questions, big and small.',
      link: '/contact/',
      ariaLabel: 'Learn more on 24/7 customer support',
      label: 'Contact Us',
    },
    {
      title: 'Free 1-Year Resizes',
      description: 'Our 1-year resizes ensure that you can find the perfect fit for free.',
      link: '/30-Day-Returns/#resizing',
      ariaLabel: 'Learn more on free 1-year resizing',
      label: 'Learn More',
    },
    {
      title: 'Free Diamond Upgrades',
      description: 'We offer a free lifetime diamond upgrade on our natural and lab grown diamonds.',
      link: '/shop-online/#diamond_upgrade',
      ariaLabel: 'Learn more on free diamond upgrades',
      label: 'Learn More',
    },
  ],
  largeCopySectionOne: {
    description: `
      <p>
        H3-Medium Lorem ipsum dolor sit amet consectetur. Non at purus pellentesque enim non nam nibh. Eget donec dolor est
        euismod ultrices rutrum quis turpis fames.
      </p>
    `,
    color: 'light-green' as ColorKeys,
  },
  moreToExplore: {
    optionalHeader: 'More to Explore',
    className: 'tw-my-14 lg:tw-my-20 tw-z-[100]',
    carouselInfo: {
      name: 'mission-carousel',
      type: 'mission' as ICarouselInfo['type'],
    },
    items: [
      {
        name: 'Engagement Rings',
        imgSrcs: categoryCarouselImage('/bee2-ui/img/mission/More-To-Explore_ER.jpg'),
        altText: 'Engagement Rings',
        link: '/pages/engagement-rings/',
      },
      {
        name: 'Gemstone Rings',
        imgSrcs: categoryCarouselImage('/bee2-ui/img/mission/More-To-Explore_Gemstone.jpg'),
        altText: 'Gemstone Rings',
        link: '/pages/gemstones/',
      },
      {
        name: 'Wedding Rings',
        imgSrcs: categoryCarouselImage('/bee2-ui/img/mission/More-To-Explore_WR.jpg'),
        altText: 'Wedding Rings',
        link: '/pages/wedding-rings/',
      },
      {
        name: 'Fine Jewelry',
        imgSrcs: categoryCarouselImage('/bee2-ui/img/mission/More-To-Explore_FJ.jpg'),
        altText: 'Fine Jewelry',
        link: '/pages/jewelry/',
      },
    ],
  },
};

export default data;
